/**
 * `<RotateSigningKeyDialog>` — typed-confirm rotation flow. High-friction
 * tier per NFR-Design § 2 + NFR-SEC-16.
 *
 * Phrase the operator must type: `rotate <workspace-slug>`.
 */

import { Label, RadioGroup, RadioGroupItem } from "@cometchat-ai/ui-dashboard";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { SigningKeyAlgorithm } from "../../lib/api/types";
import { TypedConfirmDialog } from "../common/TypedConfirmDialog";

export interface RotateSigningKeyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceSlug: string;
  busy?: boolean;
  /** True if no current key exists — switches copy/button to "Generate" semantics. */
  isGenerate?: boolean;
  onConfirm: (algorithm: SigningKeyAlgorithm) => void;
}

export function RotateSigningKeyDialog({
  open,
  onOpenChange,
  workspaceSlug,
  busy = false,
  isGenerate = false,
  onConfirm,
}: RotateSigningKeyDialogProps) {
  const { t } = useTranslation("signing-keys");
  const [algorithm, setAlgorithm] = useState<SigningKeyAlgorithm>("EdDSA");
  const phrase = `rotate ${workspaceSlug}`;

  return (
    <TypedConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title={isGenerate ? t("empty.cta") : t("rotate.title")}
      body={t("rotate.body")}
      confirmationPhrase={phrase}
      inputLabel={t("rotate.typedLabel", { phrase })}
      confirmLabel={isGenerate ? t("empty.cta") : t("rotate.confirm")}
      destructive={!isGenerate}
      busy={busy}
      onConfirm={() => onConfirm(algorithm)}
      testId="rotate-signing-key-dialog"
      extraContent={
        <fieldset className="space-y-2">
          <Label className="text-[13px] font-medium text-foreground">{t("rotate.algorithm")}</Label>
          <RadioGroup
            className="flex gap-4"
            value={algorithm}
            onValueChange={(v) => setAlgorithm(v as SigningKeyAlgorithm)}
          >
            <Label
              htmlFor="rotate-algorithm-EdDSA"
              className="flex items-center gap-2 text-[13px] font-normal"
            >
              <RadioGroupItem
                value="EdDSA"
                id="rotate-algorithm-EdDSA"
                data-testid="rotate-algorithm-EdDSA"
              />
              {t("rotate.algorithmEdDSA")}
            </Label>
            <Label
              htmlFor="rotate-algorithm-RS256"
              className="flex items-center gap-2 text-[13px] font-normal"
            >
              <RadioGroupItem
                value="RS256"
                id="rotate-algorithm-RS256"
                data-testid="rotate-algorithm-RS256"
              />
              {t("rotate.algorithmRS256")}
            </Label>
          </RadioGroup>
        </fieldset>
      }
    />
  );
}
